import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Calendar, ChevronLeft, ChevronRight, Search, Save, Users, CheckCircle2, Circle,
  Palmtree, Stethoscope, Ban, ListTree, AlignJustify,
  Baby, Flag, AlertCircle, HeartHandshake, CalendarOff, FileDown,
  Lock, CheckSquare, Square, Coffee,
} from "lucide-react";

// ---- helpers ----
const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtFullDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const initials = (n) => (n || "").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

// 12 status options. Half-day variants share a `reason` but use the `halfDay` flag.
// Each REASON maps to ONE persistence target: holiday | sick | public_holiday |
// unpaid | maternity | parental | force_majeure | compassionate | absent | day_off.
const REASONS = [
  { v: "day_off",       label: "Day Off",           icon: Coffee,         tone: "text-amber-700 border-amber-200 bg-amber-50" },
  { v: "holiday",       label: "Annual leave",      icon: Palmtree,       tone: "text-blue-700 border-blue-200 bg-blue-50" },
  { v: "sick",          label: "Sick leave",        icon: Stethoscope,    tone: "text-violet-700 border-violet-200 bg-violet-50" },
  { v: "public_holiday",label: "Public holiday",    icon: CalendarOff,    tone: "text-emerald-700 border-emerald-200 bg-emerald-50" },
  { v: "unpaid",        label: "Unpaid leave",      icon: Ban,            tone: "text-slate-700 border-slate-200 bg-slate-50" },
  { v: "maternity",     label: "Maternity leave",   icon: Baby,           tone: "text-pink-700 border-pink-200 bg-pink-50" },
  { v: "parental",      label: "Parental leave",    icon: Baby,           tone: "text-fuchsia-700 border-fuchsia-200 bg-fuchsia-50" },
  { v: "force_majeure", label: "Force majeure",     icon: AlertCircle,    tone: "text-orange-700 border-orange-200 bg-orange-50" },
  { v: "compassionate", label: "Compassionate",     icon: HeartHandshake, tone: "text-rose-700 border-rose-200 bg-rose-50" },
  { v: "absent",        label: "Absent",            icon: Flag,           tone: "text-red-700 border-red-200 bg-red-50" },
];
const reasonMeta = (v) => REASONS.find((r) => r.v === v);

// Leave types persisted via /api/leave-entries
const UNIFIED_REASONS = new Set([
  "public_holiday", "unpaid", "maternity", "parental",
  "force_majeure", "compassionate", "absent", "day_off",
]);

// Derive current state for one employee + date
function deriveState({ holiday, sick, timeEntry, leaveEntry }) {
  // A worked time entry alongside a half-day leave means "other half worked".
  const workedHalf = !!(timeEntry && Number(timeEntry.total_hours) > 0);
  // leave_entries take precedence for new leave types (public_holiday/unpaid/maternity/etc.)
  if (leaveEntry) {
    const half = Number(leaveEntry.days) === 0.5;
    return {
      working: false,
      reason: leaveEntry.leave_type,
      halfDay: half,
      otherHalf: half && workedHalf ? "worked" : "off",
      source: "server",
    };
  }
  if (holiday) {
    const half = Number(holiday.days) === 0.5;
    return {
      working: false,
      reason: "holiday",
      halfDay: half,
      otherHalf: half && workedHalf ? "worked" : "off",
      source: "server",
    };
  }
  if (sick) {
    const half = Number(sick.days) === 0.5;
    return {
      working: false,
      reason: "sick",
      halfDay: half,
      otherHalf: half && workedHalf ? "worked" : "off",
      source: "server",
    };
  }
  if (timeEntry) {
    const zero = !timeEntry.time_in || timeEntry.time_in === "00:00";
    if (zero) return { working: false, reason: "day_off", halfDay: false, otherHalf: "off", source: "server" };
    return { working: true, reason: null, halfDay: false, otherHalf: "off", source: "server" };
  }
  return { working: null, reason: null, halfDay: false, otherHalf: "off", source: "unset" };
}

export default function InOutPage() {
  const { user } = useAuth();
  const [date, _setDate] = useState(todayStr());
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [dept, _setDept] = useState("all");
  const [grouped, setGrouped] = useState(true);
  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkReason, setBulkReason] = useState("holiday");
  const [bulkHalf, setBulkHalf] = useState(false);
  const [bulkOtherHalf, setBulkOtherHalf] = useState("worked");
  // Lock state
  const [lock, setLock] = useState({ locked: false, admin_name: null });

  // Server records keyed by employee_id
  const [srvTime, setSrvTime] = useState({});
  const [srvHol, setSrvHol]   = useState({});
  const [srvSick, setSrvSick] = useState({});
  const [srvLeave, setSrvLeave] = useState({});
  // Day Off entitlement tracking (for over-allowance warnings)
  const [dayOffYtd, setDayOffYtd] = useState({});   // employee_id -> approved day_off days this year
  const [dayOffDefault, setDayOffDefault] = useState(20);

  // User edits — keyed by employee.id. Only rows the user has actively touched
  // appear here. Server-derived state for untouched rows is computed via useMemo.
  const [edits, setEdits] = useState({});

  // Wrap date/dept setters so changing the filter discards in-flight edits.
  const setDate = (v) => { setEdits({}); setSelected(new Set()); _setDate(v); };
  const setDept = (v) => { setEdits({}); setSelected(new Set()); _setDept(v); };

  // ---- Load ----
  // Effect-driven. We re-run whenever date or department changes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = { status: "active" };
        if (dept !== "all") params.department = dept;
        const [empsRes, timeRes] = await Promise.all([
          api.get("/employees", { params }),
          api.get("/time-entries", { params: { start: date, end: date } }),
        ]);
        if (cancelled) return;
        setEmployees(empsRes.data || []);
        const deptSet = new Set();
        for (const e of empsRes.data || []) if (e.department) deptSet.add(e.department);
        setDepartments(Array.from(deptSet).sort());

        const t = {}; for (const e of timeRes.data || []) t[e.employee_id] = e;
        setSrvTime(t);

        try {
          const year = Number(date.slice(0, 4));
          const h = await api.get("/holidays/entries", { params: { year } });
          if (cancelled) return;
          const map = {}; for (const x of h.data || []) if (x.date === date) map[x.employee_id] = x;
          setSrvHol(map);
        } catch { if (!cancelled) setSrvHol({}); }

        try {
          const sk = await api.get("/sick-leave");
          if (cancelled) return;
          const map = {};
          for (const x of sk.data || []) {
            if (x.start_date <= date && x.end_date >= date && !["rejected", "cancelled"].includes(x.status)) {
              map[x.employee_id] = x;
            }
          }
          setSrvSick(map);
        } catch { if (!cancelled) setSrvSick({}); }

        try {
          const year = Number(date.slice(0, 4));
          const le = await api.get("/leave-entries", { params: { year } });
          if (cancelled) return;
          const map = {};
          const doYtd = {};
          for (const x of le.data || []) {
            if (x.date === date && x.status === "approved") map[x.employee_id] = x;
            if (x.leave_type === "day_off" && x.status === "approved") {
              doYtd[x.employee_id] = (doYtd[x.employee_id] || 0) + Number(x.days || 0);
            }
          }
          setSrvLeave(map);
          setDayOffYtd(doYtd);
        } catch { if (!cancelled) { setSrvLeave({}); setDayOffYtd({}); } }

        try {
          const s = await api.get("/settings");
          if (!cancelled) setDayOffDefault(Number((s.data?.leave || {}).default_day_off_days ?? 20));
        } catch { /* keep default */ }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [date, dept]);

  // Fetch lock status when date changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/managers/lock-info", { params: { date } });
        if (!cancelled) setLock(r.data || { locked: false });
      } catch {
        if (!cancelled) setLock({ locked: false });
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  // Re-export as a callable for the saveAll flow (used after saving all rows).
  const reloadAfterSave = async () => {
    try {
      const params = { status: "active" };
      if (dept !== "all") params.department = dept;
      const timeRes = await api.get("/time-entries", { params: { start: date, end: date } });
      const t = {}; for (const e of timeRes.data || []) t[e.employee_id] = e;
      setSrvTime(t);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Derive the displayed draft from server data + user edits. User edits win.
  const draft = useMemo(() => {
    const out = {};
    for (const emp of employees) {
      if (edits[emp.id]) {
        out[emp.id] = { ...edits[emp.id], dirty: true };
      } else {
        out[emp.id] = {
          ...deriveState({
            holiday: srvHol[emp.id],
            sick: srvSick[emp.id],
            timeEntry: srvTime[emp.id],
            leaveEntry: srvLeave[emp.id],
          }),
          dirty: false,
        };
      }
    }
    return out;
  }, [employees, edits, srvTime, srvHol, srvSick, srvLeave]);

  // ---- UI helpers ----
  const isLocked = !!lock.locked;
  const setRow = (empId, patch) => {
    if (isLocked) return;
    setEdits((e) => {
      const baseFromServer = deriveState({
        holiday: srvHol[empId],
        sick: srvSick[empId],
        timeEntry: srvTime[empId],
        leaveEntry: srvLeave[empId],
      });
      const base = e[empId] || baseFromServer;
      return { ...e, [empId]: { ...base, ...patch } };
    });
  };

  const markWorking = (empId) => setRow(empId, { working: true, reason: null, halfDay: false });
  const markNotWorking = (empId, reason = "day_off") => {
    setRow(empId, { working: false, reason, halfDay: false });
    if (reason === "day_off") warnDayOffIfExceeded(empId, false);
  };

  // Day Off earned-balance helper (monthly straight-line: allowance ÷ 12 per completed month)
  const dayOffEarnedFor = (empId) => {
    const emp = employees.find((e) => e.id === empId);
    const allowed = emp && emp.day_off_entitlement_days != null
      ? Number(emp.day_off_entitlement_days) : Number(dayOffDefault);
    const y = Number(date.slice(0, 4));
    const now = new Date();
    let completed;
    if (y < now.getFullYear()) completed = 12;
    else if (y > now.getFullYear()) completed = 0;
    else completed = now.getMonth();  // months fully completed so far
    return { allowed, earned: (allowed * completed) / 12, taken: dayOffYtd[empId] || 0 };
  };
  const warnDayOffIfExceeded = (empId, halfDay) => {
    const { allowed, earned, taken } = dayOffEarnedFor(empId);
    const need = halfDay ? 0.5 : 1;
    if (earned - taken < need) {
      const emp = employees.find((e) => e.id === empId);
      toast.warning(
        `${emp?.full_name || "Employee"} is over their Day Off allowance (earned ${earned.toFixed(1)}, taken ${taken.toFixed(1)} of ${allowed}). Recorded anyway.`
      );
    }
  };
  const toggleHalfDay = (empId) => {
    if (isLocked) return;
    setEdits((e) => {
      const baseFromServer = deriveState({
        holiday: srvHol[empId],
        sick: srvSick[empId],
        timeEntry: srvTime[empId],
        leaveEntry: srvLeave[empId],
      });
      const base = e[empId] || baseFromServer;
      const nextHalf = !base.halfDay;
      // Turning half-day ON defaults the other half to "worked" (user can switch to Off).
      return { ...e, [empId]: { ...base, halfDay: nextHalf, otherHalf: nextHalf ? "worked" : "off" } };
    });
  };
  const setOtherHalf = (empId, value) => setRow(empId, { otherHalf: value });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) =>
      !q ||
      (e.full_name || "").toLowerCase().includes(q) ||
      (e.employee_id || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  // When the user is actively searching, scroll the first matching row into
  // view so they don't have to hunt for it on a phone. Only fires when:
  //   - there's a non-empty search string
  //   - at least one row matches
  //   - the row isn't already visible in the viewport
  useEffect(() => {
    const q = search.trim();
    if (!q) return;
    const id = setTimeout(() => {
      // Find the first row currently rendered in the DOM
      const firstRow = document.querySelector('[data-testid^="io-row-"]');
      if (!firstRow) return;
      const rect = firstRow.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!inView) {
        firstRow.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 220);
    return () => clearTimeout(id);
  }, [search]);

  const byDept = useMemo(() => {
    const g = new Map();
    for (const e of filtered) {
      const k = e.department || "— No department —";
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(e);
    }
    for (const [, list] of g) list.sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    return Array.from(g.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const totalDirty = Object.keys(edits).length;
  const counts = useMemo(() => {
    const c = { working: 0, notWorking: 0, unset: 0 };
    for (const e of filtered) {
      const d = draft[e.id];
      if (!d) { c.unset++; continue; }
      if (d.working === true) c.working++;
      else if (d.working === false) c.notWorking++;
      else c.unset++;
    }
    return c;
  }, [draft, filtered]);

  // ---- Bulk selection ----
  const visibleIds = useMemo(() => filtered.map((e) => e.id), [filtered]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  const toggleSelectAllVisible = () => {
    if (isLocked) return;
    setSelected((prev) => {
      const n = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };
  const toggleSelectOne = (id) => {
    if (isLocked) return;
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const bulkMark = async (action) => {
    if (isLocked) {
      toast.error(`Date locked. Contact ${lock.admin_name || "System Admin"} for changes.`);
      return;
    }
    const ids = Array.from(selected).filter((id) => visibleIds.includes(id));
    if (ids.length === 0) { toast.info("No employees selected"); return; }
    if (action === "not_working" && bulkReason === "day_off") {
      const over = ids.filter((id) => {
        const { earned, taken } = dayOffEarnedFor(id);
        return earned - taken < (bulkHalf ? 0.5 : 1);
      });
      if (over.length > 0) {
        toast.warning(`${over.length} of ${ids.length} selected are over their Day Off allowance. Recorded anyway.`);
      }
    }
    setSaving(true);
    try {
      const body = {
        date,
        employee_ids: ids,
        action,
        half_day: !!bulkHalf,
      };
      if (action === "not_working") body.reason = bulkReason;
      if (action === "not_working" && bulkHalf) body.other_half_worked = bulkOtherHalf === "worked";
      const r = await api.post("/time-entries/bulk-mark", body);
      toast.success(`Marked ${r.data?.updated ?? ids.length} as ${action === "working" ? "Working" : (REASONS.find((x) => x.v === bulkReason)?.label || "Not working")}`);
      setSelected(new Set());
      setEdits({});
      await reloadAfterSave();
      // also reload leave/holiday/sick maps since bulk-mark might clear them
      try {
        const year = Number(date.slice(0, 4));
        const [h, sk, le] = await Promise.all([
          api.get("/holidays/entries", { params: { year } }),
          api.get("/sick-leave"),
          api.get("/leave-entries", { params: { year } }),
        ]);
        const hmap = {}; for (const x of h.data || []) if (x.date === date) hmap[x.employee_id] = x;
        setSrvHol(hmap);
        const smap = {};
        for (const x of sk.data || []) if (x.start_date <= date && x.end_date >= date && !["rejected", "cancelled"].includes(x.status)) smap[x.employee_id] = x;
        setSrvSick(smap);
        const lmap = {};
        for (const x of le.data || []) if (x.date === date && x.status === "approved") lmap[x.employee_id] = x;
        setSrvLeave(lmap);
        const doYtd = {};
        for (const x of le.data || []) if (x.leave_type === "day_off" && x.status === "approved") doYtd[x.employee_id] = (doYtd[x.employee_id] || 0) + Number(x.days || 0);
        setDayOffYtd(doYtd);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ---- Save ----
  const saveRow = async (empId, opts = {}) => {
    const d = draft[empId]; if (!d?.dirty) return { ok: true, skipped: true };
    try {
      const hol = srvHol[empId];
      const sick = srvSick[empId];
      const te = srvTime[empId];
      const lv = srvLeave[empId];
      const days = d.halfDay ? 0.5 : 1.0;

      // Clear records that no longer match the chosen status.
      if (hol && d.reason !== "holiday") {
        await api.delete(`/holidays/entries/${hol.id}`);
      }
      if (sick && d.reason !== "sick") {
        await api.patch(`/sick-leave/${sick.id}/decision`, { decision: "cancel" });
      }
      if (lv && d.reason !== lv.leave_type) {
        await api.delete(`/leave-entries/${lv.id}`);
      }
      // When switching to a non-worked status, remove any worked time entry —
      // UNLESS this is a half-day leave whose OTHER half is worked.
      const keepWorkedHalf = d.working === false && d.halfDay && d.otherHalf === "worked" && d.reason !== "other";
      if (d.working === false && te && !keepWorkedHalf) {
        await api.delete(`/time-entries/${te.id}`);
      }

      if (d.working === true) {
        // Auto-fill standard paid hours via the new mark-working endpoint
        const res = await api.post("/time-entries/mark-working", {
          employee_id: empId,
          date,
          half_day: !!d.halfDay,
          force_reset: !!opts.forceReset,
        });
        if (res.data?.status === "conflict_manual") {
          return { ok: false, conflict: res.data, empId };
        }
      } else if (d.working === false) {
        if (d.reason === "holiday") {
          const needs = !hol || Number(hol.days) !== days;
          if (hol && needs) await api.delete(`/holidays/entries/${hol.id}`);
          if (needs) {
            await api.post("/holidays/entries", {
              employee_id: empId, date, days,
              notes: d.notes || (d.halfDay ? "Half-day annual leave" : "Annual leave"),
            });
          }
        } else if (d.reason === "sick") {
          const needs = !sick || Number(sick.days) !== days;
          if (sick && needs) await api.patch(`/sick-leave/${sick.id}/decision`, { decision: "cancel" });
          if (needs) {
            await api.post("/sick-leave", {
              employee_id: empId, start_date: date, end_date: date, days,
              type: "statutory", reason: d.notes || undefined,
            });
          }
        } else if (UNIFIED_REASONS.has(d.reason)) {
          const needs = !lv || lv.leave_type !== d.reason || Number(lv.days) !== days;
          if (lv && needs) await api.delete(`/leave-entries/${lv.id}`);
          if (needs) {
            await api.post("/leave-entries", {
              employee_id: empId, date, leave_type: d.reason, days,
              notes: d.notes || undefined,
            });
          }
        } else if (d.reason === "other") {
          const n = d.notes || "Other";
          await api.post("/time-entries", {
            employee_id: empId, date,
            time_in: "00:00", time_out: "00:00", break_minutes: 0, notes: n,
          });
        }
        // Half-day leave whose other half is worked: autofill a half-day worked entry.
        if (keepWorkedHalf) {
          await api.post("/time-entries/mark-working", {
            employee_id: empId, date, half_day: true, force_reset: true,
            notes: "Other half worked",
          });
        }
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, err: formatApiError(e) };
    }
  };

  const saveAll = async () => {
    if (totalDirty === 0) { toast.info("Nothing to save"); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    const conflicts = [];
    const dirtyIds = Object.keys(edits);
    for (const empId of dirtyIds) {
      const r = await saveRow(empId);
      if (r.ok) ok++;
      else if (r.conflict) conflicts.push(r);
      else fail++;
    }
    // Resolve conflicts one-by-one with a confirm prompt
    for (const c of conflicts) {
      const emp = employees.find((e) => e.id === c.empId);
      const proceed = window.confirm(
        `${emp?.full_name || "Employee"}: ${c.conflict.message}\n\n` +
        `OK = Reset to standard hours (${c.conflict.would_set.paid_hours}h)\n` +
        `Cancel = Keep current manual hours`
      );
      if (proceed) {
        const r2 = await saveRow(c.empId, { forceReset: true });
        if (r2.ok) ok++; else fail++;
      } else {
        // User kept manual values — count as ok (no-op).
        ok++;
      }
    }
    setSaving(false);
    if (fail === 0) toast.success(`Saved ${ok} row${ok === 1 ? "" : "s"}`);
    else toast.error(`Saved ${ok}, failed ${fail}`);
    // Fire submission alert (best-effort, fully non-blocking)
    if (ok > 0) {
      try {
        const empIds = dirtyIds.concat(conflicts.map((c) => c.empId));
        await api.post("/time-entries/submission", { date, employee_ids: empIds });
      } catch { /* swallow — alert is best-effort */ }
    }
    // Clear all edits — refresh time entries so saved rows pick up server values
    setEdits({});
    await reloadAfterSave();
  };

  const downloadWeeklyPdf = async () => {
    // Compute Monday of the week containing the selected date (week_start = Monday in IE).
    const d = new Date(date);
    const dayIdx = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
    const mon = new Date(d);
    mon.setDate(d.getDate() - dayIdx);
    const ws = mon.toISOString().slice(0, 10);
    try {
      const res = await api.get(`/reports/weekly-attendance.pdf?week_start=${ws}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `weekly-attendance_${ws}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Weekly PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---- Row renderer ----
  const renderRow = (emp) => {
    const d = draft[emp.id] || { working: null, reason: null, dirty: false };
    const isWorking = d.working === true;
    const isOff = d.working === false;
    const unset = d.working === null;
    const rm = isOff ? reasonMeta(d.reason) : null;
    const isSelected = selected.has(emp.id);

    return (
      <div
        key={emp.id}
        className={`flex flex-col sm:flex-row sm:items-center sm:gap-3 px-3 sm:px-4 py-3 border-b border-border last:border-b-0 ${d.dirty ? "bg-sky-50/40" : ""} ${isSelected ? "bg-amber-50/40" : ""} ${isLocked ? "opacity-80" : ""}`}
        data-testid={`io-row-${emp.employee_id}`}
      >
        {/* Identity */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleSelectOne(emp.id)}
            disabled={isLocked}
            className="shrink-0"
            data-testid={`io-select-${emp.employee_id}`}
            aria-label={`Select ${emp.full_name}`}
          />
          <div className="w-9 h-9 rounded-full bg-muted grid place-items-center text-xs font-semibold shrink-0">
            {initials(emp.full_name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{emp.full_name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {emp.department || ""}
            </div>
          </div>
          {unset && (
            <Badge variant="outline" className="text-muted-foreground sm:hidden text-[10px]">Unmarked</Badge>
          )}
        </div>

        {/* Controls — wrap to a second row on mobile, inline on sm+ */}
        <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0 sm:flex-nowrap sm:justify-end">
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <Button
              size="sm"
              variant={isWorking ? "default" : "outline"}
              onClick={() => markWorking(emp.id)}
              disabled={isLocked}
              className={`h-9 flex-1 sm:flex-none ${isWorking ? "" : "text-muted-foreground"}`}
              data-testid={`io-working-${emp.employee_id}`}
            >
              <CheckCircle2 className="w-4 h-4 sm:mr-1.5" />
              <span className="ml-1.5 sm:ml-0">Working</span>
            </Button>
            <Button
              size="sm"
              variant={isOff ? "default" : "outline"}
              onClick={() => markNotWorking(emp.id, d.reason || "day_off")}
              disabled={isLocked}
              className={`h-9 flex-1 sm:flex-none ${isOff ? "" : "text-muted-foreground"}`}
              data-testid={`io-off-${emp.employee_id}`}
            >
              <Circle className="w-4 h-4 sm:mr-1.5" />
              <span className="ml-1.5 sm:ml-0 whitespace-nowrap">Not working</span>
            </Button>
          </div>

          {/* Reason dropdown when not working — full width on mobile, fixed on sm+ */}
          {isOff && (
            <Select value={d.reason || "day_off"} onValueChange={(v) => { setRow(emp.id, { reason: v }); if (v === "day_off") warnDayOffIfExceeded(emp.id, d.halfDay); }} disabled={isLocked}>
              <SelectTrigger className={`h-9 w-full sm:w-44 ${rm?.tone || ""} font-medium`} data-testid={`io-reason-${emp.employee_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.v} value={r.v}>
                    <span className="inline-flex items-center gap-2">
                      <r.icon className="w-3.5 h-3.5" /> {r.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Half-day toggle */}
          {(isOff || isWorking) && (
            <Button
              size="sm"
              variant={d.halfDay ? "default" : "outline"}
              onClick={() => toggleHalfDay(emp.id)}
              disabled={isLocked}
              className={`h-9 px-2.5 text-xs shrink-0 ${d.halfDay ? "" : "text-muted-foreground"}`}
              title="Mark as half-day"
              data-testid={`io-halfday-${emp.employee_id}`}
            >
              ½ day
            </Button>
          )}

          {/* Other-half toggle — only for a half-day leave (not for working / day_off) */}
          {isOff && d.halfDay && (
            <div className="inline-flex items-center rounded-md border border-border overflow-hidden shrink-0 h-9" data-testid={`io-otherhalf-${emp.employee_id}`}>
              <span className="px-2 text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Other ½</span>
              <button
                type="button"
                onClick={() => setOtherHalf(emp.id, "worked")}
                disabled={isLocked}
                className={`h-9 px-2.5 text-xs ${d.otherHalf === "worked" ? "bg-emerald-600 text-white" : "text-muted-foreground"}`}
                title="Record the other half as worked"
                data-testid={`io-otherhalf-worked-${emp.employee_id}`}
              >
                Worked
              </button>
              <button
                type="button"
                onClick={() => setOtherHalf(emp.id, "off")}
                disabled={isLocked}
                className={`h-9 px-2.5 text-xs ${d.otherHalf === "off" ? "bg-slate-700 text-white" : "text-muted-foreground"}`}
                title="Leave the other half as off"
                data-testid={`io-otherhalf-off-${emp.employee_id}`}
              >
                Off
              </button>
            </div>
          )}

          {/* Unmarked badge — only on sm+ (mobile already shows it next to the name) */}
          {unset && (
            <Badge variant="outline" className="text-muted-foreground hidden sm:inline-flex">Not marked</Badge>
          )}
        </div>
      </div>
    );
  };

  // ---- render ----
  return (
    <div className="space-y-6" data-testid="in-out-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="tiny-label">Roll call</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">In / Out entry</h1>
          <p className="text-sm text-muted-foreground mt-1">Mark who was working or not for the selected date.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:pr-2" data-testid="io-counts">
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Working {counts.working}</Badge>
            <Badge variant="outline" className="text-slate-700 border-slate-200 bg-slate-50">Off {counts.notWorking}</Badge>
            <Badge variant="outline">Unmarked {counts.unset}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadWeeklyPdf} className="h-10 flex-1 sm:flex-none" data-testid="io-weekly-pdf" title="Download weekly PDF for the week containing the selected date">
              <FileDown className="w-4 h-4 sm:mr-2" />
              <span className="ml-2 sm:ml-0 hidden sm:inline">Weekly PDF (this date&apos;s week)</span>
              <span className="ml-2 sm:hidden">Weekly PDF</span>
            </Button>
            <Button onClick={saveAll} disabled={totalDirty === 0 || saving || isLocked} className="h-10 flex-1 sm:flex-none" data-testid="io-save-all">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : `Save all (${totalDirty})`}
            </Button>
          </div>
        </div>
      </div>

      {/* Lock banner — managers cannot edit past dates */}
      {isLocked && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="io-lock-banner">
          <Lock className="w-4 h-4 text-amber-700" />
          <AlertTitle className="text-amber-900">Date locked</AlertTitle>
          <AlertDescription className="text-amber-900">
            {fmtFullDate(date)} has been submitted and the calendar day has passed.
            {lock.admin_name ? ` Contact System Admin ${lock.admin_name} to request any further changes.` : " Contact your System Admin to request any further changes."}
          </AlertDescription>
        </Alert>
      )}

      {/* Bulk action toolbar — visible whenever any row is selected */}
      {someSelected && !isLocked && (
        <Card className="border-amber-200 bg-amber-50/50 shadow-none" data-testid="io-bulk-toolbar">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="text-sm font-medium" data-testid="io-bulk-count">
              {Array.from(selected).filter((id) => visibleIds.includes(id)).length} selected
            </div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="io-bulk-clear">
              Clear
            </Button>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => bulkMark("working")}
                disabled={saving}
                className="h-9"
                data-testid="io-bulk-working"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark working
              </Button>
              <div className="flex items-center gap-1.5">
                <Select value={bulkReason} onValueChange={setBulkReason}>
                  <SelectTrigger className="h-9 w-44" data-testid="io-bulk-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.v} value={r.v}>
                        <span className="inline-flex items-center gap-2">
                          <r.icon className="w-3.5 h-3.5" /> {r.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => bulkMark("not_working")}
                  disabled={saving}
                  className="h-9"
                  data-testid="io-bulk-not-working"
                >
                  <Circle className="w-4 h-4 mr-1.5" /> Mark not working
                </Button>
              </div>
              <Button
                size="sm"
                variant={bulkHalf ? "default" : "outline"}
                onClick={() => setBulkHalf((v) => !v)}
                className="h-9 px-2.5 text-xs"
                title="Apply half-day to bulk action"
                data-testid="io-bulk-halfday"
              >
                ½ day
              </Button>
              {bulkHalf && (
                <div className="inline-flex items-center rounded-md border border-border overflow-hidden h-9" data-testid="io-bulk-otherhalf">
                  <span className="px-2 text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Other ½</span>
                  <button
                    type="button"
                    onClick={() => setBulkOtherHalf("worked")}
                    className={`h-9 px-2.5 text-xs ${bulkOtherHalf === "worked" ? "bg-emerald-600 text-white" : "text-muted-foreground"}`}
                    data-testid="io-bulk-otherhalf-worked"
                  >
                    Worked
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkOtherHalf("off")}
                    className={`h-9 px-2.5 text-xs ${bulkOtherHalf === "off" ? "bg-slate-700 text-white" : "text-muted-foreground"}`}
                    data-testid="io-bulk-otherhalf-off"
                  >
                    Off
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Card className="border-border shadow-none">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto">
            <Label className="text-xs">Date</Label>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => setDate((d) => addDays(d, -1))} data-testid="io-date-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="relative flex-1 sm:flex-none">
                <Calendar className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pl-8 h-9 w-full sm:w-44" data-testid="io-date" />
              </div>
              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => setDate((d) => addDays(d, 1))} data-testid="io-date-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{fmtFullDate(date)}</div>
          </div>

          <div className="w-full sm:w-auto">
            <Label className="text-xs">Department</Label>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="h-9 w-full sm:w-48" data-testid="io-dept"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:flex-1 sm:min-w-[220px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or employee ID" className="pl-8 h-9" data-testid="io-search" />
            </div>
          </div>

          <div className="w-full sm:w-auto">
            <Label className="text-xs">Layout</Label>
            <div className="flex items-center border border-border rounded-md overflow-hidden h-9">
              <button
                type="button"
                onClick={() => setGrouped(true)}
                className={`px-3 h-full inline-flex items-center gap-1.5 text-xs flex-1 sm:flex-none justify-center ${grouped ? "bg-muted text-foreground" : "text-muted-foreground"}`}
                data-testid="io-layout-grouped"
              >
                <ListTree className="w-3.5 h-3.5" /> Grouped
              </button>
              <button
                type="button"
                onClick={() => setGrouped(false)}
                className={`px-3 h-full inline-flex items-center gap-1.5 text-xs flex-1 sm:flex-none justify-center border-l border-border ${!grouped ? "bg-muted text-foreground" : "text-muted-foreground"}`}
                data-testid="io-layout-flat"
              >
                <AlignJustify className="w-3.5 h-3.5" /> Flat
              </button>
            </div>
          </div>

          <div className="w-full sm:w-auto sm:ml-auto text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {filtered.length} {filtered.length === 1 ? "employee" : "employees"}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          {/* Select-all bar — sticky inside the list card */}
          {filtered.length > 0 && !isLocked && (
            <div className="px-3 sm:px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-3">
              <Checkbox
                checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                onCheckedChange={toggleSelectAllVisible}
                data-testid="io-select-all"
                aria-label="Select all visible"
              />
              <div className="text-xs text-muted-foreground">
                {allSelected ? "All visible selected" : (someSelected ? `${Array.from(selected).filter((id) => visibleIds.includes(id)).length} of ${visibleIds.length} selected` : "Select all visible")}
              </div>
            </div>
          )}
          {loading ? (
            <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">No employees match.</div>
          ) : grouped ? (
            byDept.map(([deptName, list]) => (
              <div key={deptName} data-testid={`io-group-${deptName}`}>
                <div className="px-4 py-2 bg-muted/40 border-y border-border text-xs font-semibold tracking-wide text-muted-foreground flex items-center justify-between">
                  <span>{deptName}</span>
                  <span>{list.length}</span>
                </div>
                {list.map(renderRow)}
              </div>
            ))
          ) : (
            filtered.slice().sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")).map(renderRow)
          )}
        </CardContent>
      </Card>

      {/* Floating Save bar — appears whenever there are unsaved edits so the
          user never has to scroll back up to find the Save button. */}
      {totalDirty > 0 && !isLocked && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-2 pointer-events-none"
          data-testid="io-sticky-save-bar"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="pointer-events-auto mx-auto max-w-3xl rounded-xl border border-border bg-card/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80 px-3 py-2 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium leading-tight truncate">
                {totalDirty} unsaved change{totalDirty === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-muted-foreground leading-tight truncate hidden sm:block">
                Keep editing other rows, then tap Save all when you&apos;re done.
              </div>
            </div>
            <Button
              onClick={saveAll}
              disabled={saving}
              className="h-10 px-4 shrink-0"
              data-testid="io-save-all-sticky"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving…" : `Save all (${totalDirty})`}
            </Button>
          </div>
        </div>
      )}
      {/* Spacer so the last row isn't hidden behind the floating bar */}
      {totalDirty > 0 && !isLocked && <div aria-hidden className="h-20" />}
    </div>
  );
}
